import React, { useContext, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { ArrowRightOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import Tooltip from '@totem/components/common/tooltip/Tooltip';
import BuildingsContext from '@totem/components/orgManagement/orgDetails/buildings/applied/buildingsContext';
import { Building } from '@totem/components/orgManagement/orgDetails/buildings/applied/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';
import logger from '@totem/utilities/logger';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const styles = {
  iconButton: {
    marginLeft: '0.4rem',
  },
};

const BuildingsTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { buildings, onUpdateBuildingNetSuiteKey, onAction } =
    useContext(BuildingsContext);
  const DND_ITEM_TYPE = 'NETSUITE_ADDRESS';

  type DragItem = {
    netSuiteAddressKey: string;
  };

  type DropRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: Building;
  };

  const handleKeyDrop = (
    organizationId: string,
    buildingId: string,
    netSuiteKeyId: string,
  ) => {
    // Assign key to building
    logger.debug('handleKeyDrop: Dropped ', netSuiteKeyId, ' on ', buildingId);

    onUpdateBuildingNetSuiteKey(
      'ADD',
      organizationId,
      buildingId,
      netSuiteKeyId,
    );
  };

  const DroppableBuildingRow = ({
    index,
    record,
    className,
    style,
    ...restProps
  }: DropRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isOver, canDrop }, drop] = useDrop<
      DragItem,
      void,
      { isOver: boolean; canDrop: boolean }
    >({
      accept: DND_ITEM_TYPE,
      canDrop: (item) => !!record?.id && !!item?.netSuiteAddressKey,
      drop: (item) => {
        if (!record?.id || !item?.netSuiteAddressKey) {
          return;
        }
        handleKeyDrop(
          record.organizationId,
          record.id,
          item.netSuiteAddressKey,
        );
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    });

    drop(ref);

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          backgroundColor: isOver && canDrop ? '#f0f5ff' : undefined,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const columns: ColumnProps<Building>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: Building) => <span>{record.name}</span>,
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) => sortStringAscending(compA.name, compB.name),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (_, record: Building) => {
        return (
          <div>
            {record.addressOne !== '' && <div>{record.addressOne}</div>}
            {record.addressTwo !== '' && <div>{record.addressTwo}</div>}
            <div>
              {record.city !== '' && <span>{record.city}</span>}
              {record.state !== '' && (
                <span style={{ marginLeft: '16px' }}>{record.state}</span>
              )}
            </div>
            {record.country !== '' && <div>{record.country}</div>}
          </div>
        );
      },
    },
    {
      title: 'NetSuite Keys',
      dataIndex: 'keys',
      key: 'keys',
      render: (_, record: Building) => {
        return (
          <div>
            {isNotNull(record.keys) &&
              record.keys
                .filter(
                  (key) => key.system === 'NetSuite' && key.instance === 'IB',
                )
                .map((key) => (
                  <div key={key.key}>
                    <Flex justify={'space-between'} align={'center'}>
                      <div>{key.key}</div>
                      <div>
                        {permissions.internalSystemAdministratorIBOrg.hasPermissions(
                          userProfile,
                        ) && (
                          <IBTooltip
                            content={
                              permissions.internalSystemAdministratorIBOrg
                                .tooltip
                            }
                          >
                            <Tooltip
                              title="Remove key from building"
                              placement="top"
                            >
                              <Button
                                type="primary"
                                shape="circle"
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  onUpdateBuildingNetSuiteKey(
                                    'DELETE',
                                    record.organizationId,
                                    record.id,
                                    key.key,
                                  )
                                }
                                style={styles.iconButton}
                              />
                            </Tooltip>
                          </IBTooltip>
                        )}
                      </div>
                    </Flex>
                  </div>
                ))}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: Building) => {
        return (
          <div>
            {permissions.internalSystemAdministratorIBOrg.hasPermissions(
              userProfile,
            ) && (
              <Tooltip title="Move Building" placement="top">
                <Button
                  type="primary"
                  shape="circle"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  onClick={() => onAction('building-move', record)}
                  style={styles.iconButton}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={isNotNull(buildings) ? buildings : []}
        loading={false}
        rowKey={(record) => record.id}
        components={{
          body: {
            row: DroppableBuildingRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
          }) as React.HTMLAttributes<HTMLElement>
        }
        pagination={{
          showSizeChanger: true,
        }}
      />
    </>
  );
};

export default BuildingsTable;
